import { type AuthorInfo, getCategoryAndSubcategory, getHeroImageUrl, type SearchArticleMeta } from "@serophin/nottldr-shared";
import { AuthorProfileImage } from "../author/AuthorProfileImage";
import { ArticleActionsMenu } from "./ArticleActionsMenu";
import { AudioPlayerButton } from "./AudioPlayerButton";
import { fallbackImage } from "./imageFallback";

export type ArticleCardProps = {
  meta: SearchArticleMeta;
  author: AuthorInfo | undefined;
  class?: string;
  preview?: boolean;
  priority?: boolean;
};

const Top = ({ meta }: { meta: SearchArticleMeta }) => (
  <div class="flex justify-between items-center mb-auto">
    <span class="bg-black/50 px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm">
      {getCategoryAndSubcategory(meta.category, meta.subcategory).subcategory.name}
    </span>
    <div class="flex items-center gap-1">
      <AudioPlayerButton iconSize={16} authorAlias={meta.authorAlias} articleId={meta.id} articleTitle={meta.title} source="card" />
      <ArticleActionsMenu meta={meta} iconClass="text-white" iconSize={16} backgroundClass="bg-black/50 hover:bg-black/75 backdrop-blur-sm" />
    </div>
  </div>
);

const Bottom = ({ meta, author }: { meta: SearchArticleMeta; author: AuthorInfo | undefined }) => (
  <div class="mt-auto">
    <h2 class="font-bold text-xl mb-1 text-shadow">{meta.title}</h2>
    <p class="italic mb-3 text-sm">{meta.tagline}</p>
    <div class="flex justify-between items-center text-xs text-gray-200">
      <div class="flex items-center space-x-2">
        {author && <AuthorProfileImage author={author} />}
        <span>{meta.authorAlias}</span>
      </div>
      <span>{meta.readingMinutes} min read</span>
    </div>
  </div>
);

export const ArticleCard = ({ meta, author, class: className = "", preview = false, priority = false }: ArticleCardProps) => (
  <a
    href={meta.path}
    class={`block ${className}`}
    id={`article-card-${meta.authorAlias}.${meta.id}`}
    data-test-click="article-card"
    data-test-article={meta.id}
    data-test-author={meta.authorAlias}
  >
    <div
      class={`group relative ${preview ? "" : "rounded-xl shadow-md shadow-gray-500/30"} overflow-hidden cursor-pointer aspect-[4/5] flex flex-col justify-end`}
    >
      <img
        src={getHeroImageUrl(meta)}
        alt={meta.title}
        class="absolute inset-0 w-full h-full object-cover z-0 transition-transform duration-300 group-hover:scale-110"
        fetchpriority={priority ? "high" : undefined}
        loading={priority ? undefined : "lazy"}
        onError={fallbackImage(800, 1000, meta.publishedAt)}
      />
      <div class="absolute inset-0 z-10 bg-gradient-to-t from-stone-800/80" />
      <div class="relative z-20 p-3 text-white flex flex-col justify-between h-full">
        {!preview && <Top meta={meta} />}
        <Bottom meta={meta} author={author} />
      </div>
    </div>
  </a>
);
